import { CalendarRange, RotateCcw } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { PRESETS, presetRange, rangeLabel } from "../lib/range";

function RangeBody({ value, onChange, idPrefix }) {
  const pick = (id) => (id === "custom" ? onChange({ ...value, preset: "custom" }) : onChange(presetRange(id)));
  const setDate = (k) => (e) => onChange({ ...value, preset: "custom", [k]: e.target.value });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => pick(p.id)}
            className={`min-h-[44px] sm:min-h-[36px] rounded-full px-4 text-sm font-semibold transition-colors ${value.preset === p.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
            data-testid={`${idPrefix}preset-${p.id}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <label className="min-w-0 space-y-1"><span className="text-[11px] font-semibold text-slate-500">Dari Tanggal</span>
          <input type="date" value={value.start} max={value.end || undefined} onChange={setDate("start")} className="w-full min-w-0 h-11 sm:h-10 rounded-lg border border-slate-200 bg-white px-2 text-sm" data-testid={`${idPrefix}start-input`} />
        </label>
        <label className="min-w-0 space-y-1"><span className="text-[11px] font-semibold text-slate-500">Sampai Tanggal</span>
          <input type="date" value={value.end} min={value.start || undefined} onChange={setDate("end")} className="w-full min-w-0 h-11 sm:h-10 rounded-lg border border-slate-200 bg-white px-2 text-sm" data-testid={`${idPrefix}end-input`} />
        </label>
        <button type="button" onClick={() => onChange(presetRange("month"))} className="h-11 sm:h-10 px-3 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 flex items-center gap-1" data-testid={`${idPrefix}reset-btn`}>
          <RotateCcw className="h-4 w-4" /> Reset
        </button>
      </div>
    </div>
  );
}

export function RangeFilter({ value, onChange }) {
  const label = rangeLabel(value);
  return (
    <div className="mb-6 rise">
      <div className="hidden md:block rounded-2xl border border-slate-200 bg-white p-4" data-testid="range-filter">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Periode: <span className="text-slate-900 normal-case tracking-normal text-sm" data-testid="range-label">{label}</span></p>
        <RangeBody value={value} onChange={onChange} idPrefix="range-" />
      </div>
      <Sheet>
        <SheetTrigger asChild>
          <button className="md:hidden w-full min-h-[48px] flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 text-left" data-testid="range-mobile-trigger">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-900"><CalendarRange className="h-4 w-4 text-emerald-600" /><span data-testid="range-mobile-label">{label}</span></span>
            <span className="text-xs font-semibold text-emerald-700">Ubah</span>
          </button>
        </SheetTrigger>
        <SheetContent side="bottom" className="rounded-t-3xl pb-8" data-testid="range-sheet">
          <SheetHeader className="text-left mb-4">
            <SheetTitle className="font-heading">Pilih Periode</SheetTitle>
            <SheetDescription>{label}</SheetDescription>
          </SheetHeader>
          <RangeBody value={value} onChange={onChange} idPrefix="range-m-" />
        </SheetContent>
      </Sheet>
    </div>
  );
}

export function MiniChart({ data }) {
  if (!data?.length) return null;
  const byMonth = data.length > 45;
  const points = byMonth
    ? Object.entries(data.reduce((m, d) => ({ ...m, [d.date.slice(0, 7)]: (m[d.date.slice(0, 7)] || 0) + d.total }), {})).map(([date, total]) => ({ date, total }))
    : data;
  const max = Math.max(...points.map((p) => p.total), 1);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-8 rise" data-testid="mini-chart">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Pendapatan per {byMonth ? "bulan" : "hari"}</p>
      <div className="flex items-end gap-1 h-24">
        {points.map((p) => (
          <div key={p.date} title={`${p.date}: Rp ${p.total.toLocaleString("id-ID")}`} className="flex-1 min-w-[4px] rounded-t bg-emerald-500/80 hover:bg-emerald-600 transition-colors" style={{ height: `${Math.max(6, (p.total / max) * 100)}%` }} />
        ))}
      </div>
    </div>
  );
}
